/**
 * Positionen erfassen — für Rechnungen und Angebote gleichermaßen.
 *
 * Die laufende Summe steht unter der Tabelle und wird bei jedem Tastendruck
 * neu gerechnet. Sie ist Vorschau: Verbindlich rechnet der Server
 * (api/src/domain/rechnung-regeln.ts), und nach dem Speichern gilt sein
 * Ergebnis. Ohne die Vorschau ließe sich ein Rechnungsentwurf aber nicht
 * prüfen, bevor er den Empfänger erreicht.
 *
 * Der Steuersatz steht je Position, nicht je Rechnung: Als Kleinunternehmer
 * sind es durchgehend 0 %, aber wer die Regelung verlässt, hat auf einer
 * Rechnung womöglich 19 % und 7 % gemischt.
 */
import type { ReactNode } from 'react';
import { alsCent, formatiereCent, istBetrag } from '../lib/geld';

export type PositionZeile = {
  bezeichnung: string;
  beschreibung: string;
  menge: string;
  einheit: string;
  einzelpreis: string;
  rabattProzent: string;
  steuersatz: string;
};

export function leerePosition(steuersatz = '0'): PositionZeile {
  return {
    bezeichnung: '',
    beschreibung: '',
    menge: '1',
    einheit: 'Stück',
    einzelpreis: '',
    rabattProzent: '0',
    steuersatz,
  };
}

/** Was eine Zeile ergibt — dieselbe Formel wie berechnePosition() im Server. */
export function rechneZeile(z: PositionZeile): {
  nettoCent: number;
  steuerCent: number;
  bruttoCent: number;
  gueltig: boolean;
} {
  const menge = zahlOderNull(z.menge);
  const preis = zahlOderNull(z.einzelpreis);
  const rabatt = zahlOderNull(z.rabattProzent) ?? 0;
  const satz = zahlOderNull(z.steuersatz) ?? 0;

  if (menge === null || preis === null) {
    return { nettoCent: 0, steuerCent: 0, bruttoCent: 0, gueltig: false };
  }

  const nettoCent = Math.round(menge * preis * 100 * ((100 - rabatt) / 100));
  const steuerCent = Math.round((nettoCent * satz) / 100);
  return { nettoCent, steuerCent, bruttoCent: nettoCent + steuerCent, gueltig: true };
}

function zahlOderNull(text: string): number | null {
  if (text.trim() === '') return null;
  if (!istBetrag(text)) return null;
  // Über alsCent und zurück, damit hier dieselbe Formaterkennung greift wie
  // im Server — „1.234,56" muss 1234,56 ergeben und nicht 1,234.
  return alsCent(text) / 100;
}

export function summiere(zeilen: PositionZeile[]): {
  nettoCent: number;
  steuerCent: number;
  bruttoCent: number;
} {
  return zeilen.reduce(
    (summe, z) => {
      const w = rechneZeile(z);
      return {
        nettoCent: summe.nettoCent + w.nettoCent,
        steuerCent: summe.steuerCent + w.steuerCent,
        bruttoCent: summe.bruttoCent + w.bruttoCent,
      };
    },
    { nettoCent: 0, steuerCent: 0, bruttoCent: 0 },
  );
}

export function Positionen({
  zeilen,
  setzeZeilen,
  mitSteuer,
}: {
  zeilen: PositionZeile[];
  setzeZeilen: (zeilen: PositionZeile[]) => void;
  /** Bei Kleinunternehmern bleibt die Steuerspalte aus — sie wäre immer 0. */
  mitSteuer: boolean;
}): ReactNode {
  const summe = summiere(zeilen);

  function aendere(index: number, feld: keyof PositionZeile, wert: string): void {
    setzeZeilen(zeilen.map((z, i) => (i === index ? { ...z, [feld]: wert } : z)));
  }

  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Positionen</h2>
        <button
          type="button"
          className="knopf klein"
          onClick={() => setzeZeilen([...zeilen, leerePosition(mitSteuer ? '19' : '0')])}
        >
          Position hinzufügen
        </button>
      </div>

      <div className="karte-koerper">
        {zeilen.length === 0 ? (
          <p className="still klein" style={{ margin: 0 }}>
            Noch keine Position. Ohne Positionen lässt sich nichts stellen.
          </p>
        ) : (
          <div className="tabelle-rahmen">
            <table className="positionen">
              <thead>
                <tr>
                  <th style={{ width: '2rem' }}>#</th>
                  <th>Bezeichnung</th>
                  <th style={{ width: '5.5rem' }}>Menge</th>
                  <th style={{ width: '6rem' }}>Einheit</th>
                  <th style={{ width: '7rem' }}>Einzelpreis</th>
                  <th style={{ width: '5rem' }}>Rabatt</th>
                  {mitSteuer ? <th style={{ width: '5rem' }}>Steuer</th> : null}
                  <th className="zahl" style={{ width: '7rem' }}>
                    Summe
                  </th>
                  <th style={{ width: '2rem' }} />
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z, i) => {
                  const w = rechneZeile(z);
                  return (
                    <tr key={i}>
                      <td className="still">{i + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={z.bezeichnung}
                          placeholder="Leistung"
                          aria-label={`Bezeichnung der Position ${i + 1}`}
                          onChange={(e) => aendere(i, 'bezeichnung', e.target.value)}
                        />
                        <input
                          type="text"
                          value={z.beschreibung}
                          placeholder="Beschreibung (optional)"
                          aria-label={`Beschreibung der Position ${i + 1}`}
                          className="klein"
                          style={{ marginTop: '2px' }}
                          onChange={(e) => aendere(i, 'beschreibung', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="zahl"
                          value={z.menge}
                          aria-label={`Menge der Position ${i + 1}`}
                          onChange={(e) => aendere(i, 'menge', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={z.einheit}
                          aria-label={`Einheit der Position ${i + 1}`}
                          onChange={(e) => aendere(i, 'einheit', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="zahl"
                          value={z.einzelpreis}
                          placeholder="0,00"
                          aria-label={`Einzelpreis der Position ${i + 1}`}
                          onChange={(e) => aendere(i, 'einzelpreis', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="zahl"
                          value={z.rabattProzent}
                          aria-label={`Rabatt der Position ${i + 1} in Prozent`}
                          onChange={(e) => aendere(i, 'rabattProzent', e.target.value)}
                        />
                      </td>
                      {mitSteuer ? (
                        <td>
                          <select
                            value={z.steuersatz}
                            aria-label={`Steuersatz der Position ${i + 1}`}
                            onChange={(e) => aendere(i, 'steuersatz', e.target.value)}
                          >
                            <option value="0">0 %</option>
                            <option value="7">7 %</option>
                            <option value="19">19 %</option>
                          </select>
                        </td>
                      ) : null}
                      <td className="zahl">
                        {w.gueltig ? (
                          formatiereCent(w.bruttoCent, false)
                        ) : (
                          <span className="still">—</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="knopf schlicht klein"
                          aria-label={`Position ${i + 1} entfernen`}
                          onClick={() => setzeZeilen(zeilen.filter((_, j) => j !== i))}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {zeilen.length > 0 ? (
        <div className="karte-fuss">
          <dl className="paare" style={{ justifyContent: 'flex-end', marginLeft: 'auto', width: 'fit-content' }}>
            <dt>Netto</dt>
            <dd className="zahl">{formatiereCent(summe.nettoCent)}</dd>
            {mitSteuer && summe.steuerCent > 0 ? (
              <>
                <dt>Umsatzsteuer</dt>
                <dd className="zahl">{formatiereCent(summe.steuerCent)}</dd>
              </>
            ) : null}
            <dt>
              <strong>Gesamt</strong>
            </dt>
            <dd className="zahl">
              <strong>{formatiereCent(summe.bruttoCent)}</strong>
            </dd>
          </dl>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Zahl aus der Datenbank in ein Eingabefeld.
 *
 * `numeric(13,4)` kommt als „85.0000" zurück, `numeric(12,3)` als „32.000".
 * Unverändert in ein Formular gestellt sieht das nach einem Fehler aus und
 * lässt sich schlecht überschreiben. Angezeigt wird deshalb die kürzeste
 * gleichwertige Schreibweise mit Dezimalkomma.
 */
export function alsEingabetext(wert: string | null | undefined): string {
  if (wert === null || wert === undefined || wert === '') return '';
  const zahl = Number.parseFloat(wert);
  if (Number.isNaN(zahl)) return wert;
  return String(zahl).replace('.', ',');
}

/** Positionen aus der API in bearbeitbare Zeilen. */
export function alsZeilen(positionen: {
  bezeichnung: string;
  beschreibung: string | null;
  menge: string;
  einheit: string | null;
  einzelpreis: string;
  rabattProzent: string | null;
  steuersatz: string;
}[]): PositionZeile[] {
  return positionen.map((p) => ({
    bezeichnung: p.bezeichnung,
    beschreibung: p.beschreibung ?? '',
    menge: alsEingabetext(p.menge) || '1',
    einheit: p.einheit ?? 'Stück',
    einzelpreis: alsEingabetext(p.einzelpreis),
    rabattProzent: alsEingabetext(p.rabattProzent) || '0',
    steuersatz: alsEingabetext(p.steuersatz) || '0',
  }));
}

/** Zeilen in die Form bringen, die die API erwartet. */
export function alsEingabe(zeilen: PositionZeile[]): Record<string, unknown>[] {
  return zeilen
    .filter((z) => z.bezeichnung.trim() !== '')
    .map((z) => ({
      bezeichnung: z.bezeichnung.trim(),
      ...(z.beschreibung.trim() ? { beschreibung: z.beschreibung.trim() } : {}),
      menge: z.menge,
      einheit: z.einheit.trim() || 'Stück',
      einzelpreis: z.einzelpreis,
      rabattProzent: Number.parseFloat(z.rabattProzent) || 0,
      steuersatz: Number.parseFloat(z.steuersatz) || 0,
    }));
}
